import { Component, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { VersionCheckService } from './Shared/_services/version-check.service';
import { UserService } from './Dashboard/_services/users.service';
import { AlertService } from './Dashboard/_services/alert.service';
import { NuaiChatComponent } from './Shared/_components/nuai-chat/nuai-chat.component';

// Pages where an unannounced reload would wipe unsaved staff work in
// progress - the reload is deferred until they navigate away from one of
// these instead of interrupting them.
const RELOAD_UNSAFE_PATHS = [/^\/sales\/create/, /^\/sales\/edit/, /^\/sales\/\d+/];

@Component({
    selector: 'app-root',
    imports: [RouterOutlet, NuaiChatComponent],
    templateUrl: './app.component.html',
    styleUrl: './app.component.css'
})
export class AppComponent implements OnInit {
  title = 'nustone-back-office';

  private platformId = inject(PLATFORM_ID);
  // A new deployment was detected while the user was on a form page - held
  // back until they leave it, rather than reloading immediately.
  private reloadPending = false;

  constructor(
    private router: Router,
    private versionCheckService: VersionCheckService,
    private userService: UserService,
    private alertService: AlertService,
  ) {}

  ngOnInit(): void {
    this.versionCheckService.startVersionCheck();
    this.watchSession();

    // No dismissible prompt here on purpose: a "later" that never gets
    // re-asked means a tab left open never updates again for that
    // deployment. Reload silently instead.
    this.versionCheckService.updateAvailable$.subscribe((updateAvailable) => {
      if (updateAvailable && isPlatformBrowser(this.platformId)) {
        this.reloadPending = true;
        this.reloadIfSafe();
      }
    });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd && isPlatformBrowser(this.platformId)) {
        this.reloadIfSafe();
      }
    });
  }

  // The API's 401 already signs out on the next request, but a tab left
  // open with nothing loading would keep showing back-office data after
  // the token expired. Check on a timer, and when the tab comes back into
  // view (background timers are throttled and a sleeping laptop skips them).
  private watchSession(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const check = () => {
      if (!this.userService.hasExpiredToken()) return;
      this.userService.setLoginStatus(null, false);
      if (!this.router.url.startsWith('/login')) {
        this.alertService.alert('Your session has expired. Please sign in again.', 'error');
        this.router.navigate(['/login']);
      }
    };
    check();
    setInterval(check, 30_000);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') check();
    });
  }

  private reloadIfSafe(): void {
    if (this.reloadPending && !RELOAD_UNSAFE_PATHS.some((p) => p.test(this.router.url))) {
      this.versionCheckService.reloadPage();
    }
  }
}
